import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../firebase";
import "./Navbar.css";
import logo from "../assets/images/logo.webp";
import userIcon from "../assets/images/user.webp";

const quotes = [
  "- Your key to academic success.",
  "- Turning study chaos into clarity.",
  "- Your notes. Your questions. Your edge.",
  "- Where preparation meets perfection.",
  "- Your roadmap to success.",
  "- Your guide to mastering concepts.",
  "- Knowledge simplified, success amplified.",
];

const COURSE_OPTIONS = [
  { value: "BALLB", label: "BALLB 5YDC" },
  { value: "BBALLB", label: "BBALLB 5YDC" },
  { value: "BCOMLLB", label: "BCOMLLB 5YDC" },
  { value: "3YDC", label: "LLB 3YDC" },
  { value: "AIBE Preparation", label: "AIBE Preparation" },
];

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [user, setUser] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [showProfileModal, setShowProfileModal] = useState(false);

  const [userProfile, setUserProfile] = useState(null);

  const isProfileValid =
    userProfile?.name?.trim() &&
    userProfile?.course &&
    (userProfile.course === "AIBE Preparation" || userProfile?.year);

  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("tejcodex_theme") === "dark",
  );

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark-theme");
      localStorage.setItem("tejcodex_theme", "dark");
    } else {
      document.body.classList.remove("dark-theme");
      localStorage.setItem("tejcodex_theme", "light");
    }
  }, [darkMode]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        const storedProfile = localStorage.getItem("tejcodex_user");
        if (storedProfile) {
          setUserProfile(JSON.parse(storedProfile));
        }
      } else {
        setUserProfile(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    setProfileMenuOpen(false);
    navigate("/");
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setQuoteIndex((prevIndex) => (prevIndex + 1) % quotes.length);
        setFade(true);
      }, 500);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".profile-section")) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <nav className="navbar">
      <div className="navbar-top">
        <div className="left-section">
          <div className="hamburger" onClick={toggleMenu}>
            &#9776;
          </div>
          <div className={`brand ${menuOpen ? "menu-open" : ""}`}>
            <h1 className="brand-title">TejCodex</h1>
            <img src={logo} alt="Logo" className="brand-logo" />
          </div>
        </div>

        {/* Right Section */}
        <div className="right-section">
          <div className="desktop-links">
            <button onClick={() => navigate("/")}>Home</button>
            <button onClick={() => navigate("/notes")}>Notes</button>
            <button onClick={() => navigate("/pyqs")}>PYQs</button>
            <button onClick={() => navigate("/aibe-preparation")}>AIBE</button>
            <button onClick={() => navigate("/about")}>About</button>
            <button onClick={() => navigate("/contact")}>Contact</button>
          </div>

          <div className="profile-section">
            <img
              src={userIcon}
              alt="user"
              className="brand-logo"
              onClick={() => setProfileMenuOpen((prev) => !prev)}
            />

            {profileMenuOpen && (
              <div className="profile-options">
                <img src={userIcon} alt="User" className="profile-icon" />
                <div className="theme-toggle-container">
                  <span>Dark Mode</span>
                  <label className="theme-switch">
                    <input
                      type="checkbox"
                      checked={darkMode}
                      onChange={() => setDarkMode(!darkMode)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                {userProfile ? (
                  <>
                    <p className="username-text">
                      Hello, <strong>{userProfile.name}</strong> 👋
                    </p>

                    <div className="profile-buttons">
                      <button onClick={() => navigate("/savednotes")}>
                        Saved Notes
                      </button>

                      <button
                        onClick={() => {
                          setShowProfileModal(true);
                          setProfileMenuOpen(false); // optional, close profile menu while modal open
                        }}
                      >
                        Update Details
                      </button>

                      <button className="logout-btn" onClick={handleLogout}>
                        Log Out
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="username-text login-warning">
                      🔓 Log in to unlock your personalized TejCodex experience
                      — 📚 save your notes, 📖 continue reading anytime, 📊
                      track what you’ve learned, and 🧠 study smarter with
                      features built just for you.
                    </p>

                    <button
                      className="login-btn"
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate("/auth");
                      }}
                    >
                      Log In
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Side Menu (mobile) */}
      {menuOpen && (
        <div
          className={`menu-overlay${menuOpen ? " open" : ""}`}
          onClick={() => setMenuOpen(false)}
        />
      )}
      <div className={`side-menu${menuOpen ? " open" : ""}`}>
        <div className="hamburgerm" onClick={toggleMenu}>
          &#9776;
        </div>
        <img src={logo} alt="Logo" className="menu-logo" />
        <h1 className="menu-title">TejCodex</h1>

        <div className={`menu-quotes ${fade ? "fade-in" : "fade-out"}`}>
          {quotes[quoteIndex]}
        </div>

        <div className="menu-link1">
          <button onClick={() => handleNavigate("/")}>Home</button>
          <button onClick={() => handleNavigate("/notes")}>Notes</button>
          <button onClick={() => handleNavigate("/pyqs")}>PYQs</button>

        </div>
        <div className="menu-link1">
          <button onClick={() => handleNavigate("/aibe-preparation")}>AIBE</button>
          <button onClick={() => handleNavigate("/about")}>About</button>
          <button onClick={() => handleNavigate("/contact")}>Contact</button>
        </div>
      </div>
      {showProfileModal && (
        <div
          className="profile-modal-overlay"
          onClick={(e) => {
            if (e.target.classList.contains("profile-modal-overlay")) {
              setShowProfileModal(false);
            }
          }}
        >
          <div className="profile-modal">
            <h2>Update Your Details</h2>
            <input
              required
              type="text"
              placeholder="Your Name"
              value={userProfile?.name || ""}
              onChange={(e) =>
                setUserProfile({ ...userProfile, name: e.target.value })
              }
            />
            <select
              required
              value={userProfile?.course || ""}
              onChange={(e) =>
                setUserProfile({
                  ...userProfile,
                  course: e.target.value,
                  year: "",
                })
              }
            >
              <option value="">Select Course</option>
              {COURSE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {userProfile?.course && userProfile.course !== "AIBE Preparation" && (
              <select
                value={userProfile?.year || ""}
                onChange={(e) =>
                  setUserProfile({ ...userProfile, year: e.target.value })
                }
              >
                <option value="">Select Year</option>
                {(userProfile.course === "3YDC"
                  ? ["1st Year", "2nd Year", "3rd Year"]
                  : ["1st Year", "2nd Year", "3rd Year", "4th Year", "5th Year"]
                ).map((yr, idx) => (
                  <option key={idx} value={yr}>
                    {yr}
                  </option>
                ))}
              </select>
            )}

            <button
              disabled={!isProfileValid}
              style={{
                opacity: !isProfileValid ? 0.6 : 1,
                cursor: !isProfileValid ? "not-allowed" : "pointer",
              }}
              onClick={() => {
                if (!userProfile?.name?.trim()) {
                  alert("Please enter your name.");
                  return;
                }
                if (!userProfile?.course) {
                  alert("Please select your course.");
                  return;
                }
                if (userProfile?.course !== "AIBE Preparation" && !userProfile?.year) {
                  alert("Please select your year.");
                  return;
                }

                localStorage.setItem(
                  "tejcodex_user",
                  JSON.stringify(userProfile),
                );
                setShowProfileModal(false);
                // Navigate reliably to home page
                navigate("/", { replace: true });
              }}
            >
              Save & Continue
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
